/**
 * Daily Generator Notification Utility
 * Handles daily PP and Shield generation for users based on their vault generator level
 */

import { db } from '../firebase';
import { doc, getDoc, runTransaction, Timestamp, serverTimestamp } from 'firebase/firestore';
import { getCurrentUTCDayStart, calculateDaysAway, calculateEarnings } from './generatorEarnings';
import { computeNextPP } from './playerPowerPoints';

export interface DailyGeneratorResult {
  daysAway: number;
  ppEarned: number;
  shieldsEarned: number;
  generatorLevel: number;
  ppPerDay: number;
  shieldsPerDay: number;
  previousPP: number;
  previousShields: number;
  newPP: number;
  newShields: number;
}

/**
 * Get today's date key in UTC (YYYY-MM-DD format)
 */
export function getTodayDateKey(): string {
  const now = new Date();
  const year = now.getUTCFullYear();
  const month = String(now.getUTCMonth() + 1).padStart(2, '0');
  const day = String(now.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Check if daily generator modal should be shown today
 * Returns true if lastDailyGeneratorModalDate is not today
 */
export function shouldShowDailyGeneratorModal(lastModalDate: string | null | undefined): boolean {
  if (!lastModalDate) {
    return true; // Never shown before
  }
  const todayKey = getTodayDateKey();
  return lastModalDate !== todayKey;
}

/**
 * Calculate and credit daily generator earnings for a user
 * Uses Firestore transaction to prevent duplicate credits
 * 
 * @param userId - User ID
 * @param generatorLevel - Generator level (from vault)
 * @param ppPerDay - PP per day rate
 * @param shieldsPerDay - Shields per day rate
 * @param vaultCapacity - Maximum PP capacity
 * @param maxShieldStrength - Maximum shield strength
 * @returns Earnings result if credits were applied, null otherwise
 */
export async function checkAndCreditDailyGenerator(
  userId: string,
  generatorLevel: number,
  ppPerDay: number,
  shieldsPerDay: number,
  vaultCapacity: number,
  maxShieldStrength: number
): Promise<DailyGeneratorResult | null> {
  try {
    const todayKey = getTodayDateKey();
    const usersRef = doc(db, 'users', userId);
    const vaultRef = doc(db, 'vaults', userId);
    const studentRef = doc(db, 'students', userId);

    let result: DailyGeneratorResult | null = null;

    await runTransaction(db, async (transaction) => {
      // Read current state
      const usersDoc = await transaction.get(usersRef);
      const vaultDoc = await transaction.get(vaultRef);
      const studentDoc = await transaction.get(studentRef);

      if (!vaultDoc.exists()) {
        // No vault, can't generate
        return;
      }

      const vaultData = vaultDoc.data();
      const usersData = usersDoc.exists() ? usersDoc.data() : {};
      // Check if modal was already shown today
      const lastModalDate = usersData.lastDailyGeneratorModalDate || null;
      if (!shouldShowDailyGeneratorModal(lastModalDate)) {
        // Already shown today, just update lastLoginAt
        if (usersDoc.exists()) {
          transaction.update(usersRef, {
            lastLoginAt: serverTimestamp()
          });
        }
        return;
      }

      // Get last claim time (prefer users.lastGeneratorClaimAt, fallback to vault.generatorLastClaimedAt)
      let lastClaimedAt: Date | null = null;
      if (usersData.lastGeneratorClaimAt) {
        const claimedAt = usersData.lastGeneratorClaimAt;
        if (claimedAt instanceof Date) {
          lastClaimedAt = claimedAt;
        } else if (claimedAt && typeof claimedAt === 'object' && 'toDate' in claimedAt) {
          lastClaimedAt = claimedAt.toDate();
        } else {
          lastClaimedAt = new Date(claimedAt as any);
        }
      } else if (vaultData.generatorLastClaimedAt) {
        const claimedAt = vaultData.generatorLastClaimedAt;
        if (claimedAt instanceof Date) {
          lastClaimedAt = claimedAt;
        } else if (claimedAt && typeof claimedAt === 'object' && 'toDate' in claimedAt) {
          lastClaimedAt = claimedAt.toDate();
        } else {
          lastClaimedAt = new Date(claimedAt as any);
        }
      } else if (usersData.lastLoginAt) {
        // Fallback to lastLoginAt if no claim time exists
        const loginAt = usersData.lastLoginAt;
        if (loginAt instanceof Date) {
          lastClaimedAt = loginAt;
        } else if (loginAt && typeof loginAt === 'object' && 'toDate' in loginAt) {
          lastClaimedAt = loginAt.toDate();
        } else {
          lastClaimedAt = new Date(loginAt as any);
        }
      } else if (usersData.createdAt) {
        // Fallback to account creation
        const createdAt = usersData.createdAt;
        if (createdAt instanceof Date) {
          lastClaimedAt = createdAt;
        } else if (createdAt && typeof createdAt === 'object' && 'toDate' in createdAt) {
          lastClaimedAt = createdAt.toDate();
        } else {
          lastClaimedAt = new Date(createdAt as any);
        }
      }

      // Calculate days away
      const daysAway = calculateDaysAway(lastClaimedAt);

      // Get current values for display (even if no earnings)
      const currentVaultPP = vaultData.currentPP || 0;
      const currentShieldStrength = vaultData.shieldStrength || 0;

      // If no days away, still show modal but with 0 earnings (first login of day)
      if (daysAway <= 0) {
        // CRITICAL FIX: Set claim timestamps even when daysAway is 0 to ensure proper tracking
        // This prevents the issue where users with null timestamps get 0 earnings forever
        const now = getCurrentUTCDayStart();
        const updates: any = {
          lastLoginAt: serverTimestamp(),
          lastDailyGeneratorModalDate: todayKey,
          lastGeneratorClaimAt: Timestamp.fromDate(now)
        };
        if (usersDoc.exists()) {
          transaction.update(usersRef, updates);
        } else {
          transaction.set(usersRef, updates);
        }
        
        // Also update vault timestamp to ensure consistency
        transaction.update(vaultRef, {
          generatorLastClaimedAt: Timestamp.fromDate(now)
        });
        
        // Return result with 0 earnings but current values for display
        result = {
          daysAway: 0,
          ppEarned: 0,
          shieldsEarned: 0,
          generatorLevel,
          ppPerDay,
          shieldsPerDay,
          previousPP: currentVaultPP,
          previousShields: currentShieldStrength,
          newPP: currentVaultPP,
          newShields: currentShieldStrength
        };
        return;
      }

      // Calculate earnings
      const { ppEarned, shieldsEarned } = calculateEarnings(daysAway, ppPerDay, shieldsPerDay);

      // Generator grant trims award overflow back to capacity; every store gets the vault balance.
      const newVaultPP = computeNextPP({
        current: currentVaultPP,
        delta: ppEarned,
        capacity: vaultCapacity,
        mode: 'generatorGrant',
      });
      const newStudentPP = newVaultPP;
      const newUsersPP = newVaultPP;
      const newShieldStrength = Math.min(maxShieldStrength, currentShieldStrength + shieldsEarned);

      // Update timestamp to start of current UTC day
      const now = getCurrentUTCDayStart();

      // Update vault
      transaction.update(vaultRef, {
        currentPP: newVaultPP,
        shieldStrength: newShieldStrength,
        generatorLastClaimedAt: Timestamp.fromDate(now)
      });

      // Update student PP
      if (studentDoc.exists()) {
        transaction.update(studentRef, {
          powerPoints: newStudentPP
        });
      }

      // Update users collection
      const usersUpdates: any = {
        lastLoginAt: serverTimestamp(),
        lastGeneratorClaimAt: Timestamp.fromDate(now),
        lastDailyGeneratorModalDate: todayKey
      };
      if (usersDoc.exists()) {
        // Update existing users doc
        if (usersData.powerPoints !== undefined) {
          usersUpdates.powerPoints = newUsersPP;
        }
        transaction.update(usersRef, usersUpdates);
      } else {
        // Create users doc if it doesn't exist
        transaction.set(usersRef, {
          ...usersUpdates,
          powerPoints: newUsersPP
        });
      }

      // Set result for return (include previous and new values for modal display)
      result = {
        daysAway,
        ppEarned,
        shieldsEarned,
        generatorLevel,
        ppPerDay,
        shieldsPerDay,
        previousPP: currentVaultPP,
        previousShields: currentShieldStrength,
        newPP: newVaultPP,
        newShields: newShieldStrength
      };
    });

    return result;
  } catch (error) {
    console.error('Error checking and crediting daily generator:', error);
    return null;
  }
}


